/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
var PwcMarkdown_1;
import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import rehypeStringify from 'rehype-stringify';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import remarkGfm from 'remark-gfm';
import rehypeExternalLinks from 'rehype-external-links';
import { unified } from 'unified';
import DOMPurify from 'dompurify';
import rehypeProsMultipartJson from './rehype-pros-multipart-json.js';
/**
 * WebComponent.
 */
let PwcMarkdown = PwcMarkdown_1 = class PwcMarkdown extends LitElement {
    /**
     * Create a processor.
     */
    static _createProcessor() {
        // create an instance of the processor.
        return unified()
            .use(remarkParse)
            .use(remarkGfm)
            .use(remarkRehype, { allowDangerousHtml: false })
            .use(rehypeProsMultipartJson)
            .use(rehypeExternalLinks, {
            target: '_blank',
            rel: ['nofollow', 'noreferrer'],
        })
            .use(rehypeStringify);
    }
    /**
     * @override
     */
    updated(_changedProperties) {
        // render the text.
        if (_changedProperties.has('messagePart')) {
            this._convertMessagePartToMarkdown();
        }
    }
    /**
     * Process value.
     */
    async _convertMessagePartToMarkdown() {
        // create processor.
        const processor = PwcMarkdown_1._createProcessor();
        if (!this.messagePart) {
            return;
        }
        // convert.
        const file = await processor.process(this.messagePart.data);
        // sanitize.
        const sanitizedHtml = DOMPurify.sanitize(String(file), {
            ADD_ATTR: ['target', 'rel', 'ref-id'],
            ADD_TAGS: ['pwc-message-part'],
        });
        // render.
        this._markdown.innerHTML = sanitizedHtml;
        // retrieve all ui elements.
        const pwcMessageParts = this._markdown.querySelectorAll('pwc-message-part[ref-id]');
        // attach data to mardown generated dom elements.
        for (const pwcMessagePart of pwcMessageParts) {
            // get the jsonRefId
            const refId = pwcMessagePart.getAttribute('ref-id');
            // search for an existing reference.
            if (this.messagePart.messagePartsReferences) {
                // find matching id.
                const foundJsonReference = this.messagePart.messagePartsReferences.find(messagePartReference => messagePartReference.refId === refId);
                // udpdate renderer.
                if (foundJsonReference) {
                    pwcMessagePart.setMessagePart(foundJsonReference.part);
                }
            }
        }
    }
    /**
     * @inheritdoc
     */
    render() {
        return html ` <div class="markdown"></div> `;
    }
    /**
     * Get copy content.
     */
    getCopyContent() {
        return {
            plainText: this._markdown.innerText,
            htmlText: this._markdown.innerHTML,
        };
    }
};
// styles.
PwcMarkdown.styles = css `
    :host {
      display: flex;
      max-width: 100%;
    }

    .markdown {
      max-width: 100%;
      word-wrap: break-word;
    }

    .markdown img {
      max-width: 100%;
    }

    .markdown p {
      max-width: 100%;
    }

    .markdown pre {
      max-width: 100%;
      overflow-x: auto;
    }

    .markdown code {
      display: inline-flex;
      max-width: 100%;
      overflow-x: auto;
    }

    .markdown table {
      display: inline-block;
      max-width: 100%;
      overflow-x: auto;
      border-spacing: 0px;
      border-collapse: collapse;
      border-radius: 3px;
    }

    .markdown table tr {
      border-spacing: 0px;
    }

    .markdown table td,
    .markdown table th {
      border: 1px solid #e6e6e6;
      padding: 5px;
    }

    .markdown table th {
      background-color: #f5f5f5;
    }
  `;
__decorate([
    property({ attribute: false })
], PwcMarkdown.prototype, "messagePart", void 0);
__decorate([
    query('.markdown', true)
], PwcMarkdown.prototype, "_markdown", void 0);
PwcMarkdown = PwcMarkdown_1 = __decorate([
    customElement('pwc-markdown')
], PwcMarkdown);
export { PwcMarkdown };
//# sourceMappingURL=pwc-markdown.js.map